const TIME_RE = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

export const ISO_WEEKDAYS = [
  { value: 1, short: 'Mon', label: 'Monday' },
  { value: 2, short: 'Tue', label: 'Tuesday' },
  { value: 3, short: 'Wed', label: 'Wednesday' },
  { value: 4, short: 'Thu', label: 'Thursday' },
  { value: 5, short: 'Fri', label: 'Friday' },
  { value: 6, short: 'Sat', label: 'Saturday' },
  { value: 7, short: 'Sun', label: 'Sunday' },
];

export function createAvailabilitySlot(index = 0) {
  return {
    id: `session-${Date.now()}-${index}`,
    label: '',
    weekdays: [],
    startTime: '',
    endTime: '',
    assignmentClosesMinutesBeforeStart: 0,
    location: '',
    responsibleTarget: null,
    active: true,
    sortOrder: index * 10,
  };
}

export function createAvailabilityPolicy(timezone = 'UTC') {
  return {
    enabled: false,
    timezone,
    assignmentMode: 'next-eligible',
    version: 1,
    slots: [],
    autoReschedule: {
      enabled: false,
      maxAttempts: 1,
      escalationTypeId: null,
    },
  };
}

export function validateAvailabilityPolicyForForm(policy) {
  if (!policy?.enabled) return [];
  const errors = [];
  if (!policy.timezone) errors.push('A school timezone is required.');
  if (!Array.isArray(policy.slots) || policy.slots.length === 0) {
    errors.push('Add at least one session.');
    return errors;
  }

  const ids = new Set();
  policy.slots.forEach((slot, index) => {
    const name = slot.label?.trim() || `Session ${index + 1}`;
    if (!slot.id || ids.has(slot.id)) errors.push(`${name} needs a unique identifier.`);
    if (slot.id) ids.add(slot.id);
    if (!slot.label?.trim()) errors.push(`Session ${index + 1} needs a label.`);
    if (!Array.isArray(slot.weekdays) || slot.weekdays.length === 0) {
      errors.push(`${name} needs at least one weekday.`);
    }
    if (!TIME_RE.test(slot.startTime || '') || !TIME_RE.test(slot.endTime || '')) {
      errors.push(`${name} needs valid start and end times.`);
    } else if (slot.endTime <= slot.startTime) {
      errors.push(`${name} must end after it starts.`);
    }
    const cutoff = slot.assignmentClosesMinutesBeforeStart ?? 0;
    if (!Number.isInteger(Number(cutoff)) || Number(cutoff) < 0) {
      errors.push(`${name} needs a whole-number assignment cutoff of zero or more.`);
    }
  });
  if (!policy.slots.some(slot => slot.active !== false)) {
    errors.push('At least one session must be active.');
  }
  const automatic = policy.autoReschedule;
  if (automatic?.enabled) {
    const attempts = Number(automatic.maxAttempts);
    if (!Number.isInteger(attempts) || attempts < 1 || attempts > 5) {
      errors.push('Automatic rescheduling needs an attempt limit from 1 to 5.');
    }
  }
  return errors;
}

export function weeklyAvailabilityPreview(policy) {
  return ISO_WEEKDAYS.map(day => ({
    ...day,
    slots: (policy?.slots || [])
      .filter(slot => slot.active !== false && slot.weekdays?.includes(day.value))
      .sort((a, b) => a.startTime.localeCompare(b.startTime) || (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
  }));
}

export function normalizeAvailabilityPolicy(policy, previousPolicy = null) {
  const normalizedSlots = (policy.slots || []).map((slot, index) => ({
    id: slot.id,
    label: slot.label.trim(),
    weekdays: [...slot.weekdays].sort((a, b) => a - b),
    startTime: slot.startTime,
    endTime: slot.endTime,
    assignmentClosesMinutesBeforeStart: Number(slot.assignmentClosesMinutesBeforeStart ?? 0),
    location: slot.location?.trim() || null,
    responsibleTarget: slot.responsibleTarget || null,
    active: slot.active !== false,
    sortOrder: index * 10,
  }));
  const content = {
    enabled: policy.enabled === true,
    timezone: policy.timezone,
    assignmentMode: 'next-eligible',
    slots: normalizedSlots,
    autoReschedule: {
      enabled: policy.autoReschedule?.enabled === true,
      maxAttempts: Number(policy.autoReschedule?.maxAttempts || 1),
      escalationTypeId:
        policy.autoReschedule?.escalationTypeId || null,
    },
  };
  const previousContent = previousPolicy ? {
    enabled: previousPolicy.enabled === true,
    timezone: previousPolicy.timezone,
    assignmentMode: previousPolicy.assignmentMode || 'next-eligible',
    slots: (previousPolicy.slots || []).map((slot, index) => ({
      id: slot.id,
      label: slot.label?.trim() || '',
      weekdays: [...(slot.weekdays || [])].sort((a, b) => a - b),
      startTime: slot.startTime,
      endTime: slot.endTime,
      assignmentClosesMinutesBeforeStart: Number(slot.assignmentClosesMinutesBeforeStart ?? 0),
      location: slot.location?.trim() || null,
      responsibleTarget: slot.responsibleTarget || null,
      active: slot.active !== false,
      sortOrder: index * 10,
    })),
    autoReschedule: {
      enabled: previousPolicy.autoReschedule?.enabled === true,
      maxAttempts: Number(
        previousPolicy.autoReschedule?.maxAttempts || 1
      ),
      escalationTypeId:
        previousPolicy.autoReschedule?.escalationTypeId || null,
    },
  } : null;
  const unchanged = previousContent && JSON.stringify(content) === JSON.stringify(previousContent);
  return {
    ...content,
    version: unchanged ? (previousPolicy.version || 1) : (previousPolicy?.version || 0) + 1,
  };
}
